<template>
  <div class="heatmap_stream" :class="{ 'has-active': actionData.activeIndex !== null }" @mousemove="onContainerMouseMove"
       @click="setActiveIndex(null)">
    <svg :width="containerWidth" :height="containerHeight">
      <path v-for="(item, index) in svgPathItems" :key="`path-${index}`" ref="path" class="point_path" fill="transparent"
            stroke="#fff" stroke-dasharray="4 4" stroke-opacity="0.8" :class="cssClassList[index]"
            :stroke-width="Math.max(1, 2 * containerScale)" :d="item.d"
            @mouseenter="onPathMouseEnter(index, item, cssClassList[index], $event)"
            @mouseleave="onPathMouseLeave(index, item)" />
    </svg>
    <HeatMapStreamNode v-for="(item, index) in pointItems" :key="index" :cssClass="cssClassList[index]"
                       :containerScale="containerScale" :item="item" :index="index" :pointItems="pointItems" :actionData="actionData"
                       :overlapIndexList="overlapNodeMap[`${item.x}x${item.y}`]" :containerWidth="containerWidth"
                       :containerHeight="containerHeight" :isHeatmapDownloading="isHeatmapDownloading"
                       @setHoverIndex="setHoverIndex($event)" @onPointMouseDown="onPointMouseDown" @onPointMouseUp="onPointMouseUp"
                       @onOverlapIndexClick="onOverlapIndexClick" />
    <ui-tooltip v-for="tooltipIndex in [0, 1]" :key="`tooltip-${tooltipIndex}`" ref="tooltip" class="offscreen-tooltip"
                noApplyCenterAnimate :arrowCenter="tooltipData[tooltipIndex] && tooltipData[tooltipIndex].arrow.isCenter"
                :arrowRight="tooltipData[tooltipIndex] && tooltipData[tooltipIndex].arrow.isRight"
                :arrowTop="tooltipData[tooltipIndex] && tooltipData[tooltipIndex].arrow.isTop"
                :style="tooltipData[tooltipIndex] ? tooltipData[tooltipIndex].style : null">
      {{ $t('reporting.stream.outOfArea') }}
    </ui-tooltip>
  </div>
</template>

<script>
import { getCurvedPathItems } from '@/libs/svg-helper'
import HeatMapStreamNode from './HeatMapStreamNode'
import UiTooltip from '@/components/ui/UiTooltip'

export default {
  name: 'HeatmapStream',
  components: {
    HeatMapStreamNode,
    UiTooltip
  },
  props: [
    'streamData',
    'device',
    'scale',
    'containerScale',
    'containerWidth',
    'containerHeight',
    'activeSelector',
    'userActionData',
    'scrollContainer',
    'isHeatmapDownloading',
    'path'
  ],
  data () {
    return {
      radius: 48,
      radiusActive: 74,
      pointItems: [],
      svgPathItems: [],
      cssClassList: [],
      moveIndex: null,
      clickOffset: null,
      tooltipData: [],
      tooltipRect: null,
      overlapNodeMap: {}
    }
  },
  computed: {
    pathItems () {
      if (this.streamData && this.streamData.path) {
        return this.streamData.path
      }
      return []
    },
    actionData () {
      if (this.userActionData) {
        return this.userActionData
      }

      return {
        hoverIndex: null,
        activeIndex: null
      }
    }
  },
  watch: {
    scale () {
      this.clearStream()
      this.$nextTick().then(this.renderStream)
    },
    streamData () {
      this.clearStream()
      this.$nextTick().then(this.renderStream)
    },
    actionData (data) {
      this.setCssClassList()

      if (!data.isHeatmapAction) {
        if (data.hoverIndex !== null && data.activeIndex !== data.hoverIndex) {
          // let scrollTop = null
          // let scrollLeft = null

          if (this.scrollContainer) {
            // scrollTop = this.scrollContainer.scrollTop
            // scrollLeft = this.scrollContainer.scrollLeft
          }

          const point = this.pointItems[data.hoverIndex]

          const coordsCenter = {
            left: point.x,
            top: point.y
          }

          const tooltipData = []

          if (point.isOffScreen) {
            const prevPoint = this.pathItems[data.hoverIndex - 1]
            const nextPoint = this.pathItems[data.hoverIndex + 1]

            if (prevPoint && !prevPoint.isOffScreen) {
              const pathEl = this.$refs.path[data.hoverIndex - 1]
              const point = this.getVisiblePathPoint(pathEl, true)

              if (point) {
                tooltipData.push(this.getTooltipData(point))
              }
            }

            if (nextPoint && !nextPoint.isOffScreen) {
              const pathEl = this.$refs.path[data.hoverIndex]
              const point = this.getVisiblePathPoint(pathEl)

              if (point) {
                tooltipData.push(this.getTooltipData(point))
              }
            }
          }

          if (tooltipData.length) {
            this.tooltipData = tooltipData
          }
          this.$emit('onHeatmapCenterChange', coordsCenter)
        } else {
          this.tooltipData = []
        }
      }
    },
    isHeatmapDownloading (value) {
      if (value) {
        this.setHoverIndex(null)
        this.setActiveIndex(null)
        this.clearMovedPath()
      }
    }
  },
  created () {
    this.renderStream()
  },
  methods: {
    clearStream () {
      this.pointItems = []
      this.svgPathItems = []
      this.cssClassList = []
      this.moveIndex = null
    },
    renderStream () {
      const pointItems = []

      for (const item of this.pathItems) {
        pointItems.push({
          x: item.coords.x * this.scale,
          y: item.coords.y * this.scale,
          temp: null,
          cssClass: null,
          isOffScreen: item.isOffScreen
        })
      }
      this.pointItems = pointItems
      this.setOverlapNodeMap()
      this.setSvgPathItems()
      this.setCssClassList()
    },
    setOverlapNodeMap () {
      const data = {}

      for (let i = this.pointItems.length - 1; i >= 0; i--) {
        const point = this.pointItems[i]

        if (point.temp) {
          continue
        }

        const key = `${point.x}x${point.y}`

        if (!data[key]) {
          data[key] = []
        }

        data[key].push(i)
      }

      for (const [key, value] of Object.entries(data)) {
        if (value.length === 1) {
          delete data[key]
        }
      }
      this.overlapNodeMap = data
    },
    setSvgPathItems () {
      const points = this.pointItems.map(item => {
        return item.temp || {
          x: item.x,
          y: item.y
        }
      })
      const items = getCurvedPathItems(points).map(item => {
        return {
          ...item,
          cssClass: null
        }
      })
      this.svgPathItems = items
    },
    moveSvgPath (index, x, y) {
      const targetPoint = this.pointItems[index]
      const isInitialMove = !targetPoint.temp

      targetPoint.temp = {
        x,
        y
      }

      if (isInitialMove) {
        this.setOverlapNodeMap()
        this.setCssClassList()
      }

      const tempPointItems = this.pointItems.map(item => {
        return item.temp || {
          x: item.x,
          y: item.y
        }
      })
      const items = []
      let point

      for (let i = -2; i <= 2; i++) {
        point = tempPointItems[index + i]

        if (point) {
          items.push({
            x: i === 0 ? x : point.x,
            y: i === 0 ? y : point.y
          })
        }
      }

      const paths = getCurvedPathItems(items)
      let indexOffset = -2

      switch (index) {
        case 0:
          indexOffset = 0
          break
        case 1:
          indexOffset = -1
          break
      }

      for (let i = 0; i < paths.length; i++) {
        let pathIndex = i + index + indexOffset
        this.svgPathItems[pathIndex].d = paths[i].d
      }
    },
    setCssClassList () {
      const classList = []
      const hoverIndex = this.actionData.hoverIndex
      const activeIndex = this.actionData.activeIndex
      // const isHeatmapAction = this.actionData.isHeatmapAction

      for (let i = 0; i < this.pointItems.length; i++) {
        const point = this.pointItems[i]
        const move = this.moveIndex === i
        const hover = hoverIndex === i
        const active = activeIndex === i
        const activePrev = activeIndex === i + 1
        const activeNext = activeIndex === i - 1
        const offScreen = point.isOffScreen
        const offScreenNext = this.pointItems[i + 1] && this.pointItems[i + 1].isOffScreen
        const overlapKey = `${point.x}x${point.y}`
        const overlapData = this.overlapNodeMap[overlapKey]
        let overlap = false
        let hide = false

        if (overlapData) {
          if (hover) {
            if (point.temp) {
              overlap = false
            } else {
              overlap = true
            }
          } else {
            if (overlapData.includes(activeIndex)) {
              const topIndex = overlapData.find(index => {
                const point = this.pointItems[index]
                return !point.temp && index !== activeIndex
              })
              overlap = topIndex !== undefined && topIndex === i
            } else {
              if (overlapData.includes(hoverIndex)) {
                const topIndex = overlapData.find(index => {
                  const point = this.pointItems[index]
                  return !point.temp && index !== hoverIndex
                })
                overlap = topIndex !== undefined && topIndex === i
              } else {
                overlap = overlapData[0] === i

                if (!overlap) {
                  const topPoint = this.pointItems[overlapData[0]]

                  if (topPoint.temp && overlapData[1] === i) {
                    overlap = true
                  }
                }
              }
            }
          }
          hide = !overlap
        }

        const cssClass = {
          overlap,
          hide,
          move,
          hover,
          active,
          'active-prev': activePrev,
          'active-next': activeNext,
          'off-screen': offScreen,
          'off-screen-next': offScreenNext,
          inactive: activeIndex !== null && !active && !activePrev && !activeNext
        }
        classList.push(cssClass)
      }
      this.cssClassList = classList
    },
    setHoverIndex (index) {
      const data = {
        hoverIndex: index,
        activeIndex: this.actionData.activeIndex,
        isHeatmapAction: true
      }
      this.$emit('onUserActionDataChange', data)
    },
    setActiveIndex (index) {
      const data = {
        hoverIndex: this.actionData.hoverIndex,
        activeIndex: index,
        isHeatmapAction: true
      }
      this.$emit('onUserActionDataChange', data)
    },
    onPointMouseDown (index, event) {
      window.clearTimeout(this.moveTimer)
      this.moveIndex = index
      const rect = event.currentTarget.getBoundingClientRect()
      this.clickOffset = {
        x: event.clientX - rect.left - (rect.width / 2),
        y: event.clientY - rect.top - (rect.width / 2)
      }
      this.setActiveIndex(index)
      this.setCssClassList()
    },
    onPointMouseUp () {
      this.moveIndex = null
      this.clickOffset = null
      this.setCssClassList()
      this.initMoveTimer()
    },
    onContainerMouseMove (event) {
      if (this.moveIndex !== null) {
        event.preventDefault()
        window.clearTimeout(this.moveTimer)
        const rect = event.currentTarget.getBoundingClientRect()
        const x = (event.clientX - rect.left) - this.clickOffset.x
        const y = (event.clientY - rect.top) - this.clickOffset.y
        this.moveSvgPath(this.moveIndex, x, y)
      }
    },
    initMoveTimer () {
      window.clearTimeout(this.moveTimer)

      this.moveTimer = window.setTimeout(() => {
        this.clearMovedPath()
      }, 2000)
    },
    clearMovedPath () {
      const ts = new Date().getTime()

      const move = () => {
        const timeout = 200
        const duration = new Date().getTime() - ts
        const points = this.pointItems.filter(item => !!item.temp)

        for (const point of points) {
          if (duration > timeout) {
            point.temp = null
          } else {
            const progress = duration / timeout
            point.temp = {
              x: point.temp.x - ((point.temp.x - point.x) * progress),
              y: point.temp.y - ((point.temp.y - point.y) * progress)
            }
          }
        }
        this.setSvgPathItems()

        if (duration <= timeout) {
          requestAnimationFrame(move)
        } else {
          this.setOverlapNodeMap()
          this.setCssClassList()
        }
      }
      requestAnimationFrame(move)
      this.setSvgPathItems()
    },
    getVisiblePathPoint (el, fromEnd) {
      const totalLength = el.getTotalLength()
      let len = 0
      let point = el.getPointAtLength(len)

      while (len < totalLength) {
        len += 20

        if (fromEnd) {
          point = el.getPointAtLength(totalLength - len)
        } else {
          point = el.getPointAtLength(len)
        }

        if (point.x > 0 && point.y > 0) {
          return point
        }
      }
      return null
    },
    onOverlapIndexClick (index) {
      this.$emit('onUserActionDataChange', {
        hoverIndex: null,
        activeIndex: index,
        isHeatmapAction: true
      })
    },
    getTooltipData (point) {
      if (!this.tooltipRect) {
        this.tooltipRect = {
          width: this.$refs.tooltip[0].$el.offsetWidth,
          height: this.$refs.tooltip[0].$el.offsetHeight
        }
      }

      const tooltipRect = this.tooltipRect

      let isTop = false
      let isCenter = true
      let isLeft = false
      let isRight = false
      let top
      let left

      if (point.y < tooltipRect.height + 20) {
        isTop = true
        top = point.y + 25
      } else {
        top = point.y - tooltipRect.height - 14
      }

      const minPaddingH = tooltipRect.width / 2 + 10

      if (point.x < minPaddingH) {
        isCenter = false
        isLeft = true
        isRight = false
      }

      if (this.containerWidth - point.x < minPaddingH) {
        isCenter = false
        isLeft = false
        isRight = true
      }

      if (isLeft) {
        left = point.x - 18
        let minLeft = 5

        if (this.containerScale < 1 && this.scrollContainer) {
          minLeft = -(this.scrollContainer.offsetWidth - this.containerWidth) / 2 + 15
        }
        left = Math.max(left, minLeft)
      } else if (isRight) {
        left = point.x - tooltipRect.width + 20

        let maxLeft = this.containerWidth - tooltipRect.width - 5

        if (this.containerScale < 1 && this.scrollContainer) {
          maxLeft = this.containerWidth + ((this.scrollContainer.offsetWidth - this.containerWidth) / 2) - tooltipRect.width - 15
        }
        left = Math.min(left, maxLeft)
      } else {
        left = point.x - (tooltipRect.width / 2)
      }

      return {
        style: {
          left: `${left}px`,
          top: `${top}px`
        },
        arrow: {
          isCenter,
          isRight,
          isTop
        }
      }
    },
    onPathMouseEnter (index, item, cssClass, event) {
      if (!cssClass['off-screen'] && !cssClass['off-screen-next']) {
        return
      }

      const hoverPoint = {
        x: event.offsetX,
        y: event.offsetY
      }

      this.tooltipData = [this.getTooltipData(hoverPoint)]
      window.clearTimeout(this.tooltipTimer)
    },
    onPathMouseLeave (index, item) {
      this.tooltipTimer = window.setTimeout(() => {
        this.tooltipData = []
      }, 500)
    }
  }
}
</script>

<style lang="scss" scoped>
.heatmap_stream {
  position: relative;
  height: 100%;

  >svg {
    overflow: visible;
  }

  &.has-active {
    .point_path {
      opacity: 0.5;

      &.active,
      &.active-prev {
        opacity: 1;
      }
    }
  }

  .point_path {
    pointer-events: stroke;

    &.active {
      stroke-dasharray: none;
      stroke-opacity: 1;
      stroke: #ffd400;
      stroke-width: 3px;
    }

    &.active-prev {
      stroke-dasharray: none;
      stroke-opacity: 1;
      stroke: #ffd400;
      stroke-width: 3px;
    }
  }

  .offscreen-tooltip {
    position: absolute;
    z-index: 100;
    left: -99999px;
  }
}
</style>
